import { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'

export default function Login({ error, csrfToken, signedInAs = null }) {
  const [magicLinkLoading, setMagicLinkLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [recoveryLoading, setRecoveryLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const passwordInputRef = useRef(null)
  const passwordEmailRef = useRef(null)

  // Filter out generic redirect messages, only show actual errors
  const showError = error && error !== 'Please sign in to continue.'

  useEffect(() => {
    if (showPassword) {
      // Move keyboard focus to the password field so reveal-then-tab isn't
      // required for keyboard users.
      const id = setTimeout(() => passwordInputRef.current?.focus(), 0)
      return () => clearTimeout(id)
    }
  }, [showPassword])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Kingdom Factor</CardTitle>
          <CardDescription>Enter your email to receive a magic link</CardDescription>
        </CardHeader>
        <CardContent>
          {showError && (
            <div className="mb-6 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          {/*
            Only rendered on the /login?switch=1 escape hatch — the controller
            sends `signedInAs` null on every ordinary (unauthenticated) visit.
            Signing in below replaces this session, so the sign-out button is
            offered first for anyone who just wants out.
          */}
          {signedInAs && (
            <div className="mb-6 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-3">
              <p>
                You're signed in as{' '}
                <span className="font-semibold">{signedInAs.name || signedInAs.email}</span>
                {signedInAs.email && signedInAs.name ? ` (${signedInAs.email})` : ''}.
                Signing in below will replace that session.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/logout"
                  method="post"
                  as="button"
                  className="rounded-md bg-amber-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-800"
                >
                  Sign out
                </Link>
                <Link
                  href={signedInAs.portalPath}
                  className="text-xs underline-offset-4 hover:underline self-center"
                >
                  Stay signed in
                </Link>
              </div>
            </div>
          )}
          <form
            action="/email-auth-request"
            method="post"
            className="space-y-6"
            onSubmit={() => setMagicLinkLoading(true)}
          >
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={magicLinkLoading}>
              {magicLinkLoading ? 'Sending...' : 'Send Magic Link'}
            </Button>
          </form>

          {!showPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(true)}
              aria-expanded={false}
              aria-controls="password-form"
              className="mt-4 w-full text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
            >
              Or sign in with password
            </button>
          )}

          {showPassword && (
            <>
              <div className="relative my-6 flex items-center" aria-hidden="true">
                <div className="flex-grow border-t border-border" />
                <span className="mx-3 text-xs uppercase text-muted-foreground">or</span>
                <div className="flex-grow border-t border-border" />
              </div>

              <form
                id="password-form"
                action="/login/password"
                method="post"
                className="space-y-4"
                onSubmit={() => setPasswordLoading(true)}
              >
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <div className="space-y-2">
                  <Label htmlFor="password-email">Email address</Label>
                  <Input
                    id="password-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    ref={passwordEmailRef}
                    required
                    autoComplete="username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    ref={passwordInputRef}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Signing in...' : 'Sign in with password'}
                </Button>
              </form>

              {/*
                task-328 U5. "Forgot password?" recovery. Posts the entered
                email to the SAME Rodauth /email-auth-request endpoint the
                magic-link form uses, plus a recovery=1 marker that selects
                the reset-worded email and lands the user on the waived
                set-new-password step after they click the link. Rendered only
                inside the revealed password path so the default magic-link view
                stays uncluttered (R1). If the email is empty, defer to the
                password form's own required email field rather than firing a
                blank request.
              */}
              <form
                action="/email-auth-request"
                method="post"
                className="mt-3 text-center"
                onSubmit={(event) => {
                  if (!email.trim()) {
                    event.preventDefault()
                    passwordEmailRef.current?.focus()
                    return
                  }
                  setRecoveryLoading(true)
                }}
              >
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <input type="hidden" name="recovery" value="1" />
                <input type="hidden" name="email" value={email} />
                <button
                  type="submit"
                  disabled={recoveryLoading}
                  className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline disabled:opacity-60"
                >
                  {recoveryLoading ? 'Sending recovery link...' : 'Forgot password?'}
                </button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
